import * as T from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';
import type { SimState, Traversal } from './types';
import { stylize } from './stylize';
import { branchSag, branchStyle, branchTaper } from './branch-shape';

const assets = new Map<string, Promise<GLTF>>();
type Side = 'L' | 'R';
const SIDES: Side[] = ['L', 'R'];
const UP = new T.Vector3(0, 1, 0);
const GRAVITY = 12; // sim/canopy.cpp
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const damp = (rate: number, dt: number) => 1 - Math.exp(-rate * dt);
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const v3 = (a: ArrayLike<number>) => new T.Vector3(a[0], a[1], a[2]);
// Scratch values for the per-bone helpers (never held across calls).
const _a = new T.Vector3(), _b = new T.Vector3(), _c = new T.Vector3(), _q = new T.Quaternion(), _q2 = new T.Quaternion(), _pq = new T.Quaternion(), _m = new T.Matrix4();
const IDENTITY = new T.Quaternion();
// World transforms read straight from matrixWorld. update() keeps matrices current after every edit, so this
// avoids getWorldPosition()'s ancestor walk (O(depth) per call, which dominates on the 18-bone tail).
const _dp = new T.Vector3(), _dq = new T.Quaternion(), _ds = new T.Vector3();
const wp = (o: T.Object3D, target: T.Vector3) => target.setFromMatrixPosition(o.matrixWorld);
const wq = (o: T.Object3D, target: T.Quaternion) => { o.matrixWorld.decompose(_dp, target, _ds); return target; };
const ws = (o: T.Object3D, target: T.Vector3) => { o.matrixWorld.decompose(_dp, _dq, target); return target; };
const TAIL = Array.from({ length: 18 }, (_, i) => `tail.${String(i).padStart(2, '0')}`);
const FINGERS = ['1', '2', '3', '4'];

/** A surface a hand or tail wraps: centre of the visible bark at the contact, its axis and visible radius. */
type Contact = { center: T.Vector3; axis: T.Vector3; radius: number };
export type Emote = 'triumph' | 'surprise' | 'relief';

/**
 * Presents a simulation state with the approved GLB clips plus a procedural layer (Design Bible §4–5):
 * hook-grip hand contact on the visible bark, swing-arc beats (reach → grab impact → stretch → compression →
 * acceleration → release anticipation → flight stretch → next reach), a lagging physical tail that trails,
 * curls, reaches and wraps, landing counterbalance and gameplay-driven expressions. Everything is derived
 * from SimState, so the player, the troop, ghosts and remote monkeys all animate the same way.
 */
/** Shared Team Tag ring and Crown Tag crown parts (never disposed: every monkey reuses them). */
let markers: { ring: T.BufferGeometry; crown: T.Group; team: (color: string) => T.Material } | undefined;
function markerParts() {
    if (markers)
        return markers;
    const gold = new T.MeshStandardMaterial({ color: '#f2c14e', emissive: '#6b4a08', emissiveIntensity: .45, metalness: .55, roughness: .35 });
    const gem = new T.MeshStandardMaterial({ color: '#d8455b', emissive: '#4a0d17', roughness: .3 });
    const crown = new T.Group();
    const band = new T.Mesh(new T.CylinderGeometry(.2, .18, .1, 20, 1, true), gold);
    band.material.side = T.DoubleSide;
    crown.add(band);
    const point = new T.ConeGeometry(.045, .12, 6), jewel = new T.SphereGeometry(.03, 8, 6);
    for (let i = 0; i < 5; i++) {
        const a = i / 5 * Math.PI * 2, tip = new T.Mesh(point, gold), stone = new T.Mesh(jewel, gem);
        tip.position.set(Math.cos(a) * .19, .1, Math.sin(a) * .19);
        stone.position.set(Math.cos(a) * .2, 0, Math.sin(a) * .2);
        crown.add(tip, stone);
    }
    const teams = new Map<string, T.Material>();
    markers = {
        ring: new T.TorusGeometry(.44, .055, 6, 32),
        crown,
        team: color => {
            let m = teams.get(color);
            if (!m)
                teams.set(color, m = new T.MeshBasicMaterial({ color, transparent: true, opacity: .9, depthWrite: false }));
            return m;
        },
    };
    return markers;
}
export class Monkey {
    root = new T.Group();
    model: T.Object3D;
    mixer: T.AnimationMixer;
    actions = new Map<string, T.AnimationAction>();
    bones = new Map<string, T.Object3D>();
    current = '';
    phase = 0;
    accessory = new T.Group();
    /** Room-match markers (Team Tag ring, Crown Tag crown), separate from the cosmetic accessory. */
    private badge = new T.Group();
    private ring?: T.Mesh;
    private markKey = '';
    private dressed = '';
    /** Camera distance seen at the last render (used to exaggerate expressions so they read at gameplay scale). */
    viewDistance = 8;
    /** Traversal objects used to place hands on the visible bark and to find the next catch. */
    static world: { objects: Traversal[] } | null = null;
    private static index = new Map<number, Traversal>();
    private static indexed: Traversal[] | null = null;
    private static indexedLength = 0;
    static register(world: { objects: Traversal[] }) { Monkey.world = world; Monkey.indexed = null; }
    static object(id: number) {
        const list = Monkey.world?.objects;
        if (!list || id < 0)
            return undefined;
        if (Monkey.indexed !== list || Monkey.indexedLength !== list.length) {
            Monkey.index.clear();
            for (const o of list)
                Monkey.index.set(o.id, o);
            Monkey.indexed = list;
            Monkey.indexedLength = list.length;
        }
        const o = Monkey.index.get(id);
        return o && o.id === id ? o : list.find(o => o.id === id);
    }

    // Rig measurements taken from the bind pose.
    private palm = .11;
    private fingerBind = new Map<string, T.Quaternion>();
    private headRest = new T.Matrix4();
    private rest = new Map<string, T.Matrix4>();
    private accessoryBone = 'head';
    private tailBones: T.Object3D[] = [];
    private tailLength: number[] = [];
    private tailX: T.Vector3[] = [];
    private tailP: T.Vector3[] = [];
    private tailReady = false;
    // Presentation state.
    private facing = 0;
    private started = false;
    private reseed = false;
    private lean = new T.Vector3(0, 1, 0);
    private tilt = new T.Quaternion();
    private squash = 1;
    private squashVelocity = 0;
    private last = { grab: -1, mode: 0, p: new T.Vector3(), v: new T.Vector3(), autoTails: 0, falls: 0 };
    private catchAge = 9;
    private releaseAge = 9;
    private landAge = 9;
    private airTime = 0;
    private gripSide: Side = 'L';
    private lastGrip: Side = 'R';
    private reachSide: Side = 'L';
    private reach = 0;
    private catchOffset = new T.Vector3();
    private gripUp = new T.Vector3(0, 1, 0);
    private wristStart = new T.Vector3();
    private wrist = { L: new T.Vector3(), R: new T.Vector3() };
    private freeOffset = { L: new T.Vector3(.3, -.5, .1), R: new T.Vector3(-.3, -.5, .1) };
    private target: { o: Traversal; point: T.Vector3; distance: number } | null = null;
    private scanClock = 0;
    private panic = 0;
    private relief = 0;
    private triumph = 0;
    private surprise = 0;
    private flail = 0;
    private expression = { delight: .3, concentration: 0, surprise: 0, jaw: 0 };
    private forward = new T.Vector3(0, 0, 1);
    private right = new T.Vector3(-1, 0, 0);
    private bodyUp = new T.Vector3(0, 1, 0);
    private morphs: { influences: number[]; dictionary: Record<string, number> }[] = [];

    private constructor(gltf: GLTF, public id: string, ghost = false) {
        this.model = clone(gltf.scene);
        this.root.add(this.model);
        this.model.scale.setScalar(.70);
        this.mixer = new T.AnimationMixer(this.model);
        for (const clip of gltf.animations) {
            const action = this.mixer.clipAction(clip);
            action.setLoop(T.LoopRepeat, Infinity);
            this.actions.set(clip.name, action);
        }
        this.model.traverse(o => {
            this.bones.set(o.name, o);
            if (o instanceof T.Mesh) {
                o.castShadow = !ghost;
                o.receiveShadow = true;
                // Warm late-morning rim light separates fur from brown bark (shared per asset).
                for (const m of Array.isArray(o.material) ? o.material : [o.material])
                    if (!ghost && m instanceof T.MeshStandardMaterial && !m.userData.rim)
                        stylize(m, { rim: { color: '#ffe3b3', strength: .55, power: 2.6 } });
                if (ghost) {
                    o.material = o.material.clone();
                    o.material.transparent = true;
                    o.material.opacity = .32;
                    o.material.depthWrite = false;
                }
                if (o.morphTargetDictionary && o.morphTargetInfluences)
                    this.morphs.push({ influences: o.morphTargetInfluences, dictionary: o.morphTargetDictionary });
                // Shadow passes use orthographic cameras; only the viewer's perspective camera sets the distance.
                o.onBeforeRender = (_r, _s, camera) => {
                    if ((camera as T.PerspectiveCamera).isPerspectiveCamera)
                        this.viewDistance = camera.position.distanceTo(this.root.position);
                };
            }
        });
        this.root.add(this.accessory, this.badge);
        this.accessory.matrixAutoUpdate = false;
        this.badge.matrixAutoUpdate = false;
        // Bind-pose measurements, with the pelvis on the root as update() places it.
        this.model.position.set(0, -.65, 0);
        this.root.updateMatrixWorld(true);
        const pelvis = this.bone('pelvis');
        if (pelvis) {
            this.model.position.y -= pelvis.getWorldPosition(_a).y;
            this.root.updateMatrixWorld(true);
        }
        const hand = this.bone('hand.L'), knuckle = this.bone('finger2.01.L');
        if (hand && knuckle)
            this.palm = hand.getWorldPosition(_a).distanceTo(knuckle.getWorldPosition(_b));
        for (const side of SIDES)
            for (const f of FINGERS)
                for (const j of ['01', '02']) {
                    const b = this.bone(`finger${f}.${j}.${side}`);
                    if (b)
                        this.fingerBind.set(b.name, b.quaternion.clone());
                }
        for (const name of ['head', 'neck']) {
            const b = this.bone(name);
            if (b)
                this.rest.set(name, b.matrixWorld.clone().invert());
        }
        this.headRest.copy(this.rest.get('head') ?? new T.Matrix4());
        this.tailBones = TAIL.map(n => this.bone(n)).filter((b): b is T.Object3D => !!b);
        for (let i = 1; i < this.tailBones.length; i++)
            this.tailLength.push(this.tailBones[i].getWorldPosition(_a).distanceTo(this.tailBones[i - 1].getWorldPosition(_b)));
        this.tailX = this.tailBones.map(() => new T.Vector3());
        this.tailP = this.tailBones.map(() => new T.Vector3());
    }
    static async create(id = 'default', ghost = false) {
        const path = id === 'default' ? 'hero-r04-lod1' : `${id}-v01-lod2`;
        if (!assets.has(path))
            assets.set(path, new GLTFLoader().loadAsync(`/assets/${path}.glb`).catch(error => {
                assets.delete(path);
                throw error;
            }));
        return new Monkey(await assets.get(path)!, id, ghost);
    }
    bone(name: string) { return this.bones.get(T.PropertyBinding.sanitizeNodeName(name)); }
    dress(kind: string) {
        for (const o of [...this.accessory.children]) {
            o.traverse(c => {
                if (c instanceof T.Mesh) {
                    c.geometry.dispose();
                    (c.material as T.Material).dispose();
                }
            });
            this.accessory.remove(o);
        }
        this.dressed = kind;
        this.markKey = '';
        this.accessoryBone = kind === 'scarf' ? 'neck' : 'head';
        // Accessories are authored around the bind pose; they follow their bone from there.
        this.headRest.copy(this.rest.get(this.accessoryBone) ?? new T.Matrix4());
        const mat = new T.MeshStandardMaterial({ color: kind === 'scarf' ? '#cf704d' : '#c2a06a', roughness: .95 });
        if (kind === 'hat') {
            const brim = new T.Mesh(new T.CylinderGeometry(.38, .4, .045, 24), mat), top = new T.Mesh(new T.CylinderGeometry(.22, .26, .2, 16), mat);
            brim.position.y = 1.03;
            top.position.y = 1.13;
            this.accessory.add(brim, top);
        }
        if (kind === 'scarf') {
            const scarf = new T.Mesh(new T.TorusGeometry(.19, .057, 6, 16), mat);
            scarf.rotation.x = Math.PI / 2;
            scarf.position.y = .48;
            this.accessory.add(scarf);
        }
        if (kind === 'flowers') {
            for (let i = 0; i < 7; i++) {
                const a = i / 7 * Math.PI * 2, m = new T.Mesh(new T.SphereGeometry(.064, 7, 5), new T.MeshStandardMaterial({ color: i % 2 ? '#eac661' : '#dc9199' }));
                m.position.set(Math.cos(a) * .28, 1.02, Math.sin(a) * .28);
                this.accessory.add(m);
            }
        }
    }
    /**
     * Room-match markers: a team-coloured ring at the feet (Team Tag) and a crown on the head (Crown Tag).
     * Geometry and materials are shared by every monkey, so markers own nothing to dispose. Cheap to call
     * every frame: it only rebuilds when the marking changes.
     */
    mark(team: string | null, crowned: boolean) {
        const key = `${team ?? ''}|${crowned}|${this.dressed}`;
        if (key === this.markKey)
            return;
        this.markKey = key;
        const parts = markerParts();
        if (team) {
            if (!this.ring) {
                this.ring = new T.Mesh(parts.ring);
                this.ring.rotation.x = Math.PI / 2;
                this.ring.position.y = -.6;
                this.ring.renderOrder = 2;
            }
            this.ring.material = parts.team(team);
            this.root.add(this.ring);
        } else
            this.ring?.removeFromParent();
        this.badge.clear();
        if (crowned) {
            // Sit above a hat; otherwise rest on the head.
            const crown = parts.crown.clone();
            crown.position.y = this.dressed === 'hat' ? 1.24 : 1.06;
            this.badge.add(crown);
        }
    }
    /** One-shot gameplay reactions (e.g. fruit pickup) layered over the state-driven face and body. */
    emote(kind: Emote) {
        if (kind === 'triumph')
            this.triumph = 1.4;
        else if (kind === 'surprise')
            this.surprise = 1;
        else
            this.relief = 1;
    }
    pose(name: string) {
        if (!this.actions.has(name))
            name = 'Idle';
        if (this.current === name)
            return;
        const next = this.actions.get(name)!;
        const previous = this.actions.get(this.current);
        next.reset().setEffectiveWeight(1).play();
        if (previous)
            previous.crossFadeTo(next, .18, false);
        next.paused = !['Idle', 'Swing'].includes(name);
        this.current = name;
    }
    update(dt: number, s: SimState, yaw: number) {
        // Callers measure dt from rAF timestamps, which can run *behind* a performance.now() taken during
        // loading (the first title-menu frame arrives with dt ≈ −0.9 s). A negative dt turns every
        // exponential smoother (1 − e^(−k·dt)) into a huge gain, so sanitise it before anything integrates.
        dt = Number.isFinite(dt) ? clamp(dt, 0, .1) : 0;
        this.phase += dt;
        const p = v3(s.p), v = v3(s.v), speed = v.length();
        const grabbed = s.grab >= 0, tailGrip = grabbed && s.mode === 3;
        // ---- Events derived from consecutive states -------------------------------------------------
        const jumped = !this.started || p.distanceTo(this.last.p) > 3.5;
        const caught = !jumped && grabbed && this.last.grab !== s.grab;
        const released = !jumped && !grabbed && this.last.grab >= 0;
        const landed = !jumped && s.mode === 0 && (this.last.mode === 1) && !grabbed;
        if (jumped) {
            this.tailReady = false;
            this.catchOffset.set(0, 0, 0);
            this.facing = yaw;
            this.lean.copy(UP);
            this.tilt.identity();
        }
        this.catchAge = caught ? 0 : this.catchAge + dt;
        this.releaseAge = released ? 0 : this.releaseAge + dt;
        this.airTime = s.mode === 1 && !grabbed ? this.airTime + dt : 0;
        if (landed) {
            this.landAge = 0;
            this.squashVelocity -= Math.min(2.6, Math.max(.6, -this.last.v.y * .22));
        }
        else
            this.landAge += dt;
        if (s.autoTails > this.last.autoTails && !jumped)
            this.surprise = 1;
        if (s.falls > this.last.falls && !jumped)
            this.surprise = 1;
        if (caught) {
            // The solver pulls the body onto the rope radius in one tick; hide that pop behind a short ease.
            const predicted = this.last.p.clone().addScaledVector(this.last.v, dt);
            const pop = predicted.sub(p);
            if (pop.length() < .9)
                this.catchOffset.add(pop);
            this.squashVelocity -= Math.min(3, 1.4 + speed * .12);
            if (!tailGrip) {
                const lateral = v3(s.anchor).sub(p).dot(this.right);
                this.gripSide = this.reach > .15 ? this.reachSide : Math.abs(lateral) > .08 ? (lateral > 0 ? 'R' : 'L') : this.lastGrip === 'L' ? 'R' : 'L';
                this.lastGrip = this.gripSide;
                this.wristStart.copy(this.wrist[this.gripSide]);
            }
            if (this.panic > .3 || this.last.autoTails !== s.autoTails)
                this.relief = 1;
            else if (this.last.grab < 0 && this.releaseAge > .7)
                this.relief = Math.max(this.relief, .75);
        }
        this.catchOffset.multiplyScalar(1 - damp(13, dt));
        if (!Number.isFinite(this.catchOffset.x + this.catchOffset.y + this.catchOffset.z))
            this.catchOffset.set(0, 0, 0);
        // ---- Facing: hanging monkeys keep facing the swing plane instead of spinning at each apex ---
        let targetYaw = yaw;
        if (grabbed) {
            targetYaw = this.facing;
            if (Math.hypot(v.x, v.z) > .6) {
                targetYaw = Math.atan2(v.x, v.z);
                if (Math.cos(targetYaw - this.facing) < 0)
                    targetYaw += Math.PI;
            }
        }
        const turn = Math.atan2(Math.sin(targetYaw - this.facing), Math.cos(targetYaw - this.facing));
        this.facing += turn * damp(grabbed ? 3 : 10, dt);
        this.forward.set(Math.sin(this.facing), 0, Math.cos(this.facing));
        this.right.crossVectors(this.forward, UP);
        // ---- Swing analysis ---------------------------------------------------------------------------
        const anchor = v3(s.anchor), rope = anchor.clone().sub(p), ropeLength = Math.max(.3, rope.length());
        const ropeDir = rope.clone().divideScalar(ropeLength);
        const tangential = v.clone().addScaledVector(ropeDir, -v.dot(ropeDir));
        const tension = grabbed ? (tangential.lengthSq() / ropeLength + GRAVITY * ropeDir.y) / GRAVITY : 0;
        const ahead = grabbed ? -rope.dot(this.forward) / ropeLength : 0; // >0: body in front of the anchor
        const vForward = v.dot(this.forward);
        const anticipation = grabbed ? smooth(.1, .6, ahead) * (vForward > -1.5 ? 1 : .4) : 0;
        const compression = grabbed ? smooth(.75, .25, tension) : 0;
        const stretch = grabbed ? smooth(1.3, 2.6, tension) : 0;
        const flightStretch = !grabbed && s.mode === 1 ? Math.exp(-this.releaseAge * 3.2) * smooth(2, 9, speed) * (this.releaseAge < 2 ? 1 : 0) : 0;
        const landing = Math.exp(-this.landAge * 7) * (this.landAge < 1 ? 1 : 0);
        // ---- Next catch: the solver's in-reach candidate, else the nearest grabbable span ahead --------
        this.scanClock -= dt;
        if (this.scanClock <= 0 || caught || released) {
            this.scanClock = .1;
            this.target = this.findTarget(s, p, v);
        }
        else if (this.target)
            this.refreshTarget(p, v);
        const target = this.target && this.target.o.id !== s.grab ? this.target : null;
        // ---- Near miss: falling with nothing in reach -> wide eyes, flailing, tail reach -----------------
        const falling = s.mode === 1 && !grabbed && v.y < -3.5 && this.airTime > .3;
        this.panic += ((falling && s.candidate < 0 ? smooth(3.5, 8, -v.y) * .6 + .4 : 0) - this.panic) * damp(falling ? 6 : 3, dt);
        this.flail += dt * (8 + this.panic * 10);
        // ---- Reach toward the next catch (flight) or with the free hand (anticipation on the swing) ----
        let reachWanted = 0;
        if (target && !grabbed && s.mode === 1) {
            reachWanted = smooth(3.4, 1.1, target.distance) * smooth(.06, .2, this.releaseAge);
            if (s.candidate === target.o.id)
                reachWanted = Math.max(reachWanted, .85);
        }
        else if (target && grabbed && !tailGrip)
            reachWanted = anticipation * smooth(5.5, 2.5, target.distance) * .9;
        if (target && (this.reach < .3 || grabbed)) {
            const lateral = target.point.clone().sub(p).dot(this.right);
            this.reachSide = grabbed ? (this.gripSide === 'L' ? 'R' : 'L') : Math.abs(lateral) > .06 ? (lateral > 0 ? 'R' : 'L') : this.reachSide;
        }
        this.reach += (reachWanted - this.reach) * damp(reachWanted > this.reach ? 10 : 5, dt);
        // ---- Body lean (torso lags the hands), squash & stretch ----------------------------------------
        const leanTarget = UP.clone();
        if (grabbed) {
            // Tilt so the gripping shoulder (not the spine) sits under the hand; the head hangs beside the arm.
            // On a short (slack) rope the head would meet the bark, so the body swings further aside.
            const aside = tailGrip ? 0 : (.1 + .3 * smooth(1.5, 1.15, ropeLength)) * (this.gripSide === 'L' ? 1 : -1);
            leanTarget.copy(ropeDir).addScaledVector(this.right, aside).normalize();
        }
        else if (s.mode === 1)
            leanTarget.addScaledVector(v.clone().normalize(), .5 * smooth(2, 9, speed) * (1 - this.panic * .7)).normalize();
        else if (s.mode === 4)
            leanTarget.addScaledVector(this.forward, .12).normalize();
        else if (landing > .01)
            leanTarget.addScaledVector(this.forward, .2 * landing).normalize();
        const before = this.lean.clone();
        // First-order lag: at swing speeds the torso trails the rope by ~0.2 rad (Bible: torso lags hands).
        this.lean.lerp(leanTarget, damp(grabbed ? 22 : 9, dt));
        if (this.lean.lengthSq() < 1e-6)
            this.lean.copy(before);
        this.lean.normalize();
        // Incremental minimal rotations stay continuous through loops over the bar (no antipodal flip);
        // the twist they accumulate about the body axis bleeds away so facing stays authoritative.
        this.tilt.premultiply(_q.setFromUnitVectors(before, this.lean)).normalize();
        const twist = _q2.set(0, this.tilt.y, 0, this.tilt.w);
        if (twist.lengthSq() > 1e-8) {
            twist.normalize();
            const swing = this.tilt.clone().multiply(twist.clone().invert());
            this.tilt.copy(swing.multiply(twist.slerp(IDENTITY, damp(4, dt))));
        }
        let squashTarget = 1;
        if (grabbed)
            squashTarget = 1 + clamp((tension - 1) * .05, -.06, .09) - compression * .03;
        else if (s.mode === 1)
            squashTarget = 1 + .1 * flightStretch + .025 * smooth(4, 12, speed);
        // Stiff spring (ω = 22): semi-implicit substeps keep it stable at the 0.1 s frame-time cap.
        const steps = Math.ceil(dt * 120), h = steps ? dt / steps : 0;
        for (let i = 0; i < steps; i++) {
            this.squashVelocity += ((squashTarget - this.squash) * 22 * 22 - this.squashVelocity * 2 * .42 * 22) * h;
            this.squash = clamp(this.squash + this.squashVelocity * h, .8, 1.2);
        }
        if (!Number.isFinite(this.squash + this.squashVelocity)) {
            this.squash = 1;
            this.squashVelocity = 0;
        }
        // ---- Root placement ------------------------------------------------------------------------------
        if (!Number.isFinite(this.lean.x + this.lean.y + this.lean.z + this.tilt.x + this.tilt.y + this.tilt.z + this.tilt.w)) {
            this.lean.copy(UP);
            this.tilt.identity();
        }
        const tilt = this.tilt;
        this.root.quaternion.setFromAxisAngle(UP, this.facing).premultiply(tilt);
        const side = 1 / Math.sqrt(this.squash);
        this.root.scale.set(side, this.squash, side);
        this.root.position.copy(p).add(this.catchOffset);
        if (s.mode === 4)
            this.root.position.y += Math.sin(this.phase * 4) * .035;
        this.root.position.y -= .07 * landing;
        this.bodyUp.copy(UP).applyQuaternion(this.root.quaternion);
        this.pose(tailGrip ? 'TailHang' : grabbed ? 'Hang' : s.mode === 1 ? 'Flight' : 'Idle');
        this.mixer.update(dt);
        this.model.position.set(0, -.65, 0);
        this.root.updateMatrixWorld(true);
        // Keep locomotion centered on the physical pelvis; imported clips may include study root motion.
        const pelvis = this.bone('pelvis');
        if (pelvis) {
            const local = this.root.worldToLocal(wp(pelvis, _a));
            this.model.position.y -= local.y;
        }
        // ---- Clip-relative procedural layer (local rotations) -----------------------------------------
        const groundSpeed = Math.hypot(s.v[0], s.v[2]);
        if (s.mode === 0 && groundSpeed > .3) {
            for (const side of SIDES) {
                const sign = side === 'L' ? 1 : -1, wave = Math.sin(this.phase * groundSpeed * 2) * .48 * sign;
                this.bone(`thigh.${side}`)?.rotateX(wave);
                this.bone(`upper_arm.${side}`)?.rotateX(-wave * .55);
                this.bone(`shin.${side}`)?.rotateX(Math.max(0, -wave) * .55);
            }
        }
        if (s.mode === 4) {
            for (const side of SIDES) {
                const sign = side === 'L' ? 1 : -1;
                const stroke = Math.sin(this.phase * 5 + (sign < 0 ? Math.PI : 0));
                this.bone(`upper_arm.${side}`)?.rotateZ(sign * .7);
                this.bone(`upper_arm.${side}`)?.rotateX(-.5 + stroke * .3);
                this.bone(`forearm.${side}`)?.rotateX(-.55);
                this.bone(`thigh.${side}`)?.rotateX(.25 + stroke * .25);
                this.bone(`shin.${side}`)?.rotateX(.45);
            }
        }
        this.root.updateMatrixWorld(true);
        const bodyRight = this.right.clone().applyQuaternion(tilt);
        // ---- Legs: trail through the arc, tuck at the top, stretch in flight, kick when panicking ------
        if (s.mode !== 4 && !(s.mode === 0 && groundSpeed > .3)) {
            for (const side of SIDES) {
                const sign = side === 'L' ? 1 : -1;
                let hip = 0, knee = 0;
                if (grabbed && !tailGrip) {
                    hip = clamp(-vForward * .07, -.55, .55) + compression * .75 + anticipation * .45 - stretch * .15 + sign * .06 * Math.sin(this.phase * 2.3);
                    knee = -(.2 + compression * .9 + anticipation * .5) + stretch * .15;
                }
                else if (!grabbed && s.mode === 1) {
                    hip = -.4 * flightStretch + .55 * this.reach + this.panic * Math.sin(this.flail + sign * 1.7) * .55;
                    knee = -.15 - .6 * this.reach - this.panic * (.35 + .3 * Math.sin(this.flail * 1.3 + sign));
                }
                hip += .75 * landing;
                knee -= 1.05 * landing;
                const thigh = this.bone(`thigh.${side}`), shin = this.bone(`shin.${side}`);
                if (thigh && hip)
                    this.rotateAxis(thigh, bodyRight, hip);
                if (shin && knee)
                    this.rotateAxis(shin, bodyRight, knee);
            }
        }
        // ---- Hands ---------------------------------------------------------------------------------------
        const grip = grabbed && !tailGrip ? this.gripSide : null;
        // Level of detail: far or hidden monkeys keep the clip, body lean, grip contact and face; the free
        // limbs, tail chain and glances are skipped (the tail re-seeds from the clip when it comes back).
        const detailed = this.root.visible && this.viewDistance < 28;
        let contact: Contact | null = null;
        if (grabbed) {
            const o = Monkey.object(s.grab);
            contact = this.contact(s, o, p);
            if ((o?.type ?? s.raw?.[38]) === 2) {
                // The solver's vine runs through the pelvis; hang just behind it so it passes in front of the chest.
                const back = this.forward.clone().addScaledVector(contact.axis, -this.forward.dot(contact.axis));
                this.root.position.addScaledVector(back, -.16 * smooth(0, .12, this.catchAge));
                this.root.updateMatrixWorld(true);
            }
        }
        if (grip && contact) {
            const shoulder = this.bone(`upper_arm.${grip}`);
            const blend = smooth(0, .08, this.catchAge);
            if (shoulder) {
                // Knuckles stay planted: the grip only creeps round the bark as the body swings under it.
                const across = this.across(contact, wp(shoulder, _a));
                if (this.catchAge < .02)
                    this.gripUp.copy(across);
                else {
                    this.gripUp.addScaledVector(contact.axis, -this.gripUp.dot(contact.axis)).normalize();
                    const turn = Math.atan2(_b.crossVectors(this.gripUp, across).dot(contact.axis), this.gripUp.dot(across));
                    this.gripUp.applyAxisAngle(contact.axis, turn * damp(Math.abs(turn) > 1.4 ? 9 : 3, dt));
                }
                let hold = this.hookGrip(contact, this.gripUp);
                // The rope radius is measured to the pelvis and slightly exceeds the rig's reach. Close the gap
                // with a cartoon arm stretch (the Bible's "stretch" beat) first, then by lifting the body toward
                // the hand, but never far enough to push the head into the bark.
                const s0 = wp(shoulder, new T.Vector3()), length = this.armLength(grip);
                let gap = (hold.wrist.distanceTo(s0) - length * .985) * blend;
                if (gap > 0) {
                    const k = 1 + Math.min(.14, gap / length);
                    gap -= (k - 1) * length;
                    this.stretchArm(grip, k);
                }
                const head = this.bone('head');
                if (head) {
                    const toward = hold.wrist.clone().sub(s0).normalize(), headScale = ws(head, _b).y;
                    const h = wp(head, new T.Vector3()).addScaledVector(this.bodyUp, .11 * headScale).sub(contact.center);
                    h.addScaledVector(contact.axis, -h.dot(contact.axis));
                    const room = h.length() - contact.radius - .17 * headScale;
                    // Lift toward the hand while the head has room; back away (and stretch the arm more) when it doesn't.
                    let shift = gap > 0 ? Math.min(gap, Math.max(0, room), .5) : 0;
                    if (room < 0)
                        shift = Math.max(-.3, room) * blend;
                    if (shift) {
                        this.root.position.addScaledVector(toward, shift);
                        this.root.updateMatrixWorld(true);
                        if (shift < 0)
                            this.stretchArm(grip, 1 + Math.min(.08, -shift / length));
                        hold = this.hookGrip(contact, this.gripUp);
                    }
                }
                const wrist = this.wristStart.clone().lerp(hold.wrist, blend);
                this.reachArm(grip, wrist, 1);
                this.orientHand(grip, hold.tangent, hold.palm, blend);
                const wrap = Math.min(1.45, .052 / hold.R), base = Math.min(1.2, .03 / hold.R + .25);
                this.curl(grip, base, wrap, blend, .9 * blend);
            }
        }
        for (const side of SIDES) {
            if (!detailed)
                break;
            if (side === grip)
                continue;
            const shoulder = this.bone(`upper_arm.${side}`);
            if (!shoulder)
                continue;
            const s0 = wp(shoulder, new T.Vector3()), length = this.armLength(side), sign = side === 'L' ? -1 : 1;
            const out = bodyRight.clone().multiplyScalar(sign); // away from the body on this side
            const desired = new T.Vector3();
            let weight = 0;
            const reaching = target && side === this.reachSide ? this.reach : 0;
            if (reaching > .01) {
                desired.copy(target!.point).sub(s0);
                desired.setLength(Math.min(length * .97, desired.length()));
                weight = reaching;
            }
            if (grabbed && !tailGrip) {
                // Free arm: relaxed at the side, trailing at speed, swung up as a counterweight at the top.
                const relaxed = this.bodyUp.clone().multiplyScalar(-.55).addScaledVector(out, .32).addScaledVector(this.forward, .12);
                const trail = tangential.lengthSq() > .1 ? tangential.clone().normalize().multiplyScalar(-.35) : new T.Vector3();
                relaxed.add(trail.multiplyScalar(stretch));
                relaxed.setLength(length * .9);
                desired.lerp(relaxed, 1 - reaching);
                weight = Math.max(weight, .75);
            }
            else if (!grabbed && s.mode === 1 && flightStretch > .02 && reaching < .5) {
                const back = v.clone().normalize().multiplyScalar(-.45).addScaledVector(out, .5).addScaledVector(this.bodyUp, .15).setLength(length * .95);
                desired.lerp(back, 1 - reaching);
                weight = Math.max(weight, .7 * flightStretch);
            }
            if (landing > .02 && !grabbed) {
                desired.lerp(out.clone().multiplyScalar(.75).addScaledVector(UP, .15).addScaledVector(this.forward, .1).setLength(length * .95), landing);
                weight = Math.max(weight, .85 * landing);
            }
            const flail = Math.max(this.panic, this.relief * .5, this.surprise * .6);
            if (flail > .02 && !(s.mode === 4)) {
                const wave = new T.Vector3().addScaledVector(this.bodyUp, .45 + .35 * Math.sin(this.flail * (side === 'L' ? 1 : 1.17)))
                    .addScaledVector(out, .5 + .2 * Math.cos(this.flail * .9)).addScaledVector(this.forward, .25 * Math.sin(this.flail * .7 + 1)).setLength(length * .92);
                desired.lerp(wave, flail * (1 - reaching * .5));
                weight = Math.max(weight, flail);
            }
            if (this.triumph > .02 && side !== this.reachSide) {
                const pump = .82 + .12 * Math.sin(this.phase * 10);
                desired.lerp(this.bodyUp.clone().multiplyScalar(pump).addScaledVector(out, .25).addScaledVector(this.forward, .1).setLength(length * .95), Math.min(1, this.triumph));
                weight = Math.max(weight, Math.min(1, this.triumph));
            }
            // Smooth the free arm relative to its shoulder so fast bodies don't drag it through space.
            const offset = this.freeOffset[side];
            if (weight > .01)
                offset.lerp(desired, damp(14, dt));
            if (weight > .01) {
                this.reachArm(side, s0.clone().add(offset), Math.min(1, weight));
                const fist = Math.min(1, this.triumph);
                if (reaching > .05 && !grabbed)
                    this.curl(side, -.12, -.08, reaching, 1);
                else if (fist > .05)
                    this.curl(side, 1.3, 1.5, fist, fist);
            }
        }
        if (s.mode === 4) {
            for (const side of SIDES) {
                const sign = side === 'L' ? 1 : -1;
                const stroke = Math.sin(this.phase * 5 + (sign < 0 ? Math.PI : 0));
                const target = new T.Vector3(sign * (.48 + stroke * .08), .32, .25 + stroke * .16);
                target.applyAxisAngle(UP, this.facing).add(this.root.position);
                this.reachArm(side, target, 1);
            }
        }
        // ---- Tail -----------------------------------------------------------------------------------------
        if (detailed || tailGrip)
            this.updateTail(dt, s, p, v, contact, tailGrip, anticipation, flightStretch, landing, target, falling);
        else
            this.tailReady = false;
        // ---- Head: glance at the next catch -----------------------------------------------------------
        const head = this.bone('head');
        if (head && target && s.mode !== 4 && detailed) {
            const to = target.point.clone().sub(wp(head, _a));
            const yawTo = Math.atan2(to.dot(this.right), to.dot(this.forward));
            const look = Math.max(this.reach, grabbed ? .5 : .3) * smooth(6, 2, target.distance);
            this.rotateAxis(head, this.bodyUp, clamp(-yawTo, -.6, .6) * look * .7);
            this.rotateAxis(head, bodyRight, clamp(-Math.atan2(to.y, Math.hypot(to.dot(this.right), to.dot(this.forward))), -.5, .4) * look * .6);
        }
        // ---- Expressions ----------------------------------------------------------------------------------
        this.relief = Math.max(0, this.relief - dt * 1.1);
        this.triumph = Math.max(0, this.triumph - dt * .9);
        this.surprise = Math.max(0, this.surprise - dt * 1.3);
        const swingEffort = grabbed ? .55 + .3 * smooth(1, 3, speed) + .15 * anticipation : 0;
        const whee = s.mode === 1 && !grabbed ? smooth(3, 10, speed) * smooth(.15, .5, this.airTime) * (1 - this.panic) : 0;
        const fright = Math.max(this.panic, this.surprise);
        const joy = Math.max(this.relief, Math.min(1, this.triumph), whee * .8);
        const wanted = {
            delight: Math.max(joy, s.mode === 0 || s.mode === 5 ? .3 : 0) * (1 - fright * .8),
            concentration: swingEffort * (1 - joy * .8) * (1 - fright) + (!grabbed && this.reach > .3 ? .35 * this.reach * (1 - fright) : 0),
            surprise: fright,
            jaw: fright * .8 + joy * .45 + whee * .2 + (grabbed ? stretch * .15 : 0),
        };
        for (const k of ['delight', 'concentration', 'surprise', 'jaw'] as const) {
            const next = this.expression[k] + (wanted[k] - this.expression[k]) * damp(k === 'surprise' ? 14 : 8, dt);
            this.expression[k] = Number.isFinite(next) ? clamp(next, 0, 1.5) : 0;
        }
        // At gameplay distance the face is a few dozen pixels: push the shapes and the head scale a little.
        const far = Number.isFinite(this.viewDistance) ? smooth(3.5, 11, this.viewDistance) : 0, gain = 1 + .35 * far;
        if (head) {
            head.scale.setScalar(1 + .14 * far);
            head.updateMatrixWorld(true);
        }
        const jaw = this.bone('jaw');
        if (jaw && this.expression.jaw > .01 && head)
            this.rotateAxis(jaw, _c.set(1, 0, 0).applyQuaternion(wq(head, _q2)), -.38 * Math.min(1.2, this.expression.jaw * gain));
        const blink = fright > .3 ? 0 : Math.pow(Math.max(0, Math.cos(this.phase * 1.7)), 70);
        for (const { influences: m, dictionary: d } of this.morphs) {
            if (d.Blink !== undefined)
                m[d.Blink] = this.relief > .5 && this.relief < .62 ? 1 : blink;
            if (d.Delight !== undefined)
                m[d.Delight] = clamp(this.expression.delight * gain, 0, 1.2);
            if (d.Concentration !== undefined)
                m[d.Concentration] = clamp(this.expression.concentration * gain, 0, 1.15);
            if (d.Surprise !== undefined)
                m[d.Surprise] = clamp(this.expression.surprise * gain, 0, 1.2);
        }
        // ---- Accessories ride on their bone ------------------------------------------------------------
        const carrier = this.bone(this.accessoryBone);
        if (!Number.isFinite(this.root.position.x + this.root.position.y + this.root.position.z)) {
            // Last line of defence: never hand the renderer a non-finite rig; restart from the clip next frame.
            this.root.position.copy(p);
            this.reseed = true;
        }
        this.root.updateMatrixWorld(true);
        if (carrier && this.accessory.children.length) {
            this.accessory.matrix.copy(_m.copy(this.root.matrixWorld).invert().multiply(carrier.matrixWorld).multiply(this.headRest));
            this.accessory.updateMatrixWorld(true);
        }
        const crownBone = this.badge.children.length ? this.bone('head') : undefined, headRest = this.rest.get('head');
        if (crownBone && headRest) {
            this.badge.matrix.copy(_m.copy(this.root.matrixWorld).invert().multiply(crownBone.matrixWorld).multiply(headRest));
            this.badge.updateMatrixWorld(true);
        }
        for (const side of SIDES)
        {
            const hand = this.bone(`hand.${side}`);
            if (hand)
                wp(hand, this.wrist[side]);
        }
        this.last.grab = s.grab;
        this.last.mode = s.mode;
        this.last.p.copy(p);
        this.last.v.copy(v);
        this.last.autoTails = s.autoTails;
        this.last.falls = s.falls;
        this.started = !this.reseed;
        this.reseed = false;
    }
    /** Visible bark centre/axis/radius at the solver's contact. Branch sag, sapling flex and vine swing follow the anchor. */
    private contact(s: SimState, o: Traversal | undefined, p: T.Vector3): Contact {
        const anchor = v3(s.anchor), kind = o?.type ?? s.raw?.[38];
        if (kind === 2) {
            const n = p.clone().sub(anchor), length = n.length();
            n.divideScalar(Math.max(1e-4, length));
            // Hands hold the vine an arm's length above the shoulder; a tail holds it most of a tail above the hips.
            const reach = s.mode === 3 ? this.tailLength.reduce((a, b) => a + b, 0) * .8 : this.armLength('L') + .42;
            const along = Math.max(.25, Math.min(length, s.radius) - reach);
            return { center: anchor.addScaledVector(n, along), axis: n, radius: Math.max(o?.radius ?? .07, .07) };
        }
        if (!o)
            return { center: anchor, axis: this.right.clone(), radius: .12 };
        const axis = v3(o.b).sub(v3(o.a)).normalize(), t = s.gripT;
        if (o.type === 3) {
            anchor.y += .07 * Math.sin(Math.PI * t);
            return { center: anchor, axis, radius: o.radius * T.MathUtils.lerp(1.05, .62, t) };
        }
        const [kb, kt] = branchTaper(branchStyle(o));
        anchor.y += branchSag(o) * Math.sin(Math.PI * t);
        return { center: anchor, axis, radius: o.radius * (kb + kt) / 2 };
    }
    /**
     * Hook grip: the palm lies on the near flank, knuckles just behind the crown, fingers wrap over the top.
     * Returns the wrist (hand bone) position plus world finger/palm directions.
     */
    private hookGrip(c: Contact, up: T.Vector3) {
        const f = new T.Vector3().crossVectors(c.axis, up);
        if (f.dot(this.forward) < 0)
            f.negate();
        const alpha = 1, R = c.radius + .024;
        const radial = up.clone().multiplyScalar(Math.cos(alpha)).addScaledVector(f, -Math.sin(alpha));
        const tangent = up.clone().multiplyScalar(Math.sin(alpha)).addScaledVector(f, Math.cos(alpha));
        const knuckle = c.center.clone().addScaledVector(radial, R);
        const wrist = knuckle.addScaledVector(tangent, -this.palm).addScaledVector(radial, .01);
        return { wrist, tangent, palm: radial.negate(), R };
    }
    /** Unit direction from `from` toward the contact, perpendicular to its axis (vines: the facing side). */
    private across(c: Contact, from: T.Vector3) {
        const up = c.center.clone().sub(from);
        up.addScaledVector(c.axis, -up.dot(c.axis));
        for (const fallback of [this.forward, this.bodyUp, this.right])
            if (up.lengthSq() < .0025)
                up.copy(fallback).addScaledVector(c.axis, -fallback.dot(c.axis));
        return up.normalize();
    }
    /** Lengthen the upper arm and forearm along their bone axis; the hand keeps its size. */
    private stretchArm(side: Side, k: number) {
        const upper = this.bone(`upper_arm.${side}`), fore = this.bone(`forearm.${side}`), hand = this.bone(`hand.${side}`);
        if (!upper || !fore || !hand)
            return;
        upper.scale.y *= k;
        fore.scale.y *= k;
        hand.scale.y /= k * k;
        upper.updateMatrixWorld(true);
    }
    private armLength(side: Side) {
        const u = this.bone(`upper_arm.${side}`), f = this.bone(`forearm.${side}`), h = this.bone(`hand.${side}`);
        if (!u || !f || !h)
            return .8;
        return wp(u, _a).distanceTo(wp(f, _b)) + wp(f, _a).distanceTo(wp(h, _c));
    }
    /** Rotate a joint (in world space) so `end` points at `target`. */
    private aim(joint: T.Object3D, end: T.Vector3, target: T.Vector3, weight = 1) {
        if (!joint.parent)
            return;
        const origin = wp(joint, _a);
        const from = _b.copy(end).sub(origin), to = _c.copy(target).sub(origin);
        const a = from.lengthSq(), b = to.lengthSq();
        if (!(a > 1e-10 && b > 1e-10 && a < 1e6 && b < 1e6) || !(weight > 0))
            return; // degenerate or non-finite: leave the joint as the clip posed it
        _q.setFromUnitVectors(from.normalize(), to.normalize());
        if (weight < 1)
            _q.slerp(IDENTITY, 1 - weight);
        this.rotateWorld(joint, _q);
    }
    private rotateAxis(joint: T.Object3D, axis: T.Vector3, angle: number) {
        if (!joint.parent)
            return;
        if (!Number.isFinite(angle) || !(axis.lengthSq() > 1e-10))
            return;
        this.rotateWorld(joint, _q.setFromAxisAngle(_a.copy(axis).normalize(), angle));
    }
    private rotateWorld(joint: T.Object3D, delta: T.Quaternion) {
        const parent = wq(joint.parent!, _pq);
        _q2.copy(parent).invert().multiply(delta).multiply(parent);
        if (!Number.isFinite(_q2.x + _q2.y + _q2.z + _q2.w))
            return;
        joint.quaternion.premultiply(_q2).normalize();
        joint.updateMatrixWorld(true);
    }
    /** Analytic two-bone arm IK that keeps the clip's elbow plane; the clavicle shrugs toward far targets. */
    private reachArm(side: Side, target: T.Vector3, weight: number) {
        const clavicle = this.bone(`clavicle.${side}`), upper = this.bone(`upper_arm.${side}`), fore = this.bone(`forearm.${side}`), hand = this.bone(`hand.${side}`);
        if (!upper || !fore || !hand || !(weight > .001) || !Number.isFinite(target.x + target.y + target.z))
            return;
        const saved = [upper.quaternion.clone(), fore.quaternion.clone(), hand.quaternion.clone()];
        const handQ = wq(hand, new T.Quaternion());
        if (clavicle)
            this.aim(clavicle, wp(hand, new T.Vector3()), target, .22 * weight);
        for (let pass = 0; pass < 2; pass++) {
            const s = wp(upper, new T.Vector3()), e = wp(fore, new T.Vector3()), h = wp(hand, new T.Vector3());
            const l1 = s.distanceTo(e), l2 = e.distanceTo(h);
            const toTarget = target.clone().sub(s), distance = clamp(toTarget.length(), Math.abs(l1 - l2) + .01, (l1 + l2) * .999);
            const dir = toTarget.normalize();
            const cos = clamp((l1 * l1 + distance * distance - l2 * l2) / (2 * l1 * distance), -1, 1);
            const pole = e.clone().sub(s).addScaledVector(this.bodyUp, -.05);
            pole.addScaledVector(dir, -pole.dot(dir));
            if (pole.lengthSq() < 1e-6)
                pole.copy(this.forward).multiplyScalar(-1).addScaledVector(dir, this.forward.dot(dir));
            pole.normalize();
            const elbow = s.clone().addScaledVector(dir, cos * l1).addScaledVector(pole, Math.sqrt(1 - cos * cos) * l1);
            this.aim(upper, e, elbow);
            this.aim(fore, wp(hand, new T.Vector3()), s.clone().addScaledVector(dir, distance));
        }
        // Keep the hand's world orientation from the clip; callers override it for contacts.
        const parent = wq(hand.parent!, new T.Quaternion());
        hand.quaternion.copy(parent.invert().multiply(handQ));
        if (weight < 1) {
            upper.quaternion.copy(saved[0].slerp(upper.quaternion, weight));
            fore.quaternion.copy(saved[1].slerp(fore.quaternion, weight));
            hand.quaternion.copy(saved[2].slerp(hand.quaternion, weight));
        }
        upper.updateMatrixWorld(true);
    }
    /** Orient the hand: local +Y (fingers) along `fingers`, local +Z (palm) along `palm`. */
    private orientHand(side: Side, fingers: T.Vector3, palm: T.Vector3, weight: number) {
        const hand = this.bone(`hand.${side}`);
        if (!hand?.parent || weight <= 0)
            return;
        const y = fingers.clone().normalize(), z = palm.clone().addScaledVector(y, -palm.dot(y));
        if (!(z.lengthSq() > 1e-8) || !Number.isFinite(y.x + y.y + y.z))
            return;
        z.normalize();
        const x = new T.Vector3().crossVectors(y, z);
        const world = new T.Quaternion().setFromRotationMatrix(new T.Matrix4().makeBasis(x, y, z));
        const local = wq(hand.parent, new T.Quaternion()).invert().multiply(world);
        hand.quaternion.slerp(local, weight);
        hand.updateMatrixWorld(true);
    }
    /** Finger flexion about the knuckle axis (+X curls toward the palm on both hands). */
    private curl(side: Side, knuckle: number, middle: number, weight: number, spread = 1) {
        for (const f of FINGERS) {
            const spreadAngle = (Number(f) - 2.5) * .06 * spread;
            for (const [joint, angle] of [['01', knuckle], ['02', middle]] as const) {
                const b = this.bone(`finger${f}.${joint}.${side}`), bind = b && this.fingerBind.get(b.name);
                if (!b || !bind)
                    continue;
                _q.copy(bind).multiply(_q2.setFromEuler(new T.Euler(angle, 0, joint === '01' ? spreadAngle : 0)));
                b.quaternion.slerp(_q, weight);
            }
        }
        this.bone(`hand.${side}`)?.updateMatrixWorld(true);
    }
    private findTarget(s: SimState, p: T.Vector3, v: T.Vector3) {
        const list = Monkey.world?.objects;
        if (!list || s.mode === 4)
            return null;
        if (s.candidate >= 0 && s.grab < 0) {
            const o = Monkey.object(s.candidate);
            if (o)
                return this.targetOn(o, p, v);
        }
        const grabbed = s.grab >= 0, flat = Math.hypot(v.x, v.z);
        // Hanging: look along the facing direction; flying: along the velocity.
        const heading = grabbed || flat < .5 ? this.forward.clone() : new T.Vector3(v.x / flat, 0, v.z / flat);
        const probe = p.clone().addScaledVector(heading, grabbed ? 2.2 : .3 + Math.min(1.2, flat * .12)).add(new T.Vector3(0, grabbed ? .6 : .3, 0));
        let best: { o: Traversal; point: T.Vector3; distance: number } | null = null, bestScore = Infinity;
        const limit = grabbed ? 5.5 : 3.6;
        for (const o of list) {
            if (![1, 2, 3, 7].includes(o.type) || o.id === s.grab)
                continue;
            const point = new T.Line3(v3(o.a), v3(o.b)).closestPointToPoint(probe, true, new T.Vector3());
            const delta = point.clone().sub(p), distance = delta.length();
            if (distance > limit || point.y < p.y - .4 || delta.x * heading.x + delta.z * heading.z < .15)
                continue;
            const score = point.distanceTo(probe);
            if (score < bestScore) {
                bestScore = score;
                best = { o, point, distance };
            }
        }
        return best;
    }
    private targetOn(o: Traversal, p: T.Vector3, v: T.Vector3) {
        const point = new T.Line3(v3(o.a), v3(o.b)).closestPointToPoint(p.clone().addScaledVector(v, .12), true, new T.Vector3());
        if (o.type === 2)
            point.copy(v3(o.a)).lerp(v3(o.b), clamp((p.y + .9 - o.a[1]) / Math.min(-.01, o.b[1] - o.a[1]), 0, 1));
        return { o, point, distance: point.distanceTo(p) };
    }
    private refreshTarget(p: T.Vector3, v: T.Vector3) {
        if (!this.target)
            return;
        const next = this.targetOn(this.target.o, p, v);
        this.target.point.lerp(next.point, .5);
        this.target.distance = this.target.point.distanceTo(p);
    }
    /**
     * Tail as a lagging chain: clip shape + gravity + inertia, shaped to trail in flight, hang and curl on
     * the swing (tighter in release anticipation), reach for branches on a near miss, lift to counterbalance
     * landings, and coil around the bark when it holds the monkey.
     */
    private updateTail(dt: number, s: SimState, p: T.Vector3, v: T.Vector3, contact: Contact | null, tailGrip: boolean, anticipation: number, flightStretch: number, landing: number, target: { point: T.Vector3; distance: number } | null, falling: boolean) {
        const bones = this.tailBones, n = bones.length;
        if (n < 4)
            return;
        const pose = bones.map(b => wp(b, new T.Vector3()));
        const x = this.tailX, previous = this.tailP;
        if (!this.tailReady || dt <= 0) {
            for (let i = 0; i < n; i++) {
                x[i].copy(pose[i]);
                previous[i].copy(pose[i]);
            }
            this.tailReady = true;
        }
        const grabbed = s.grab >= 0;
        // Desired shape: blend the clip's tail with a procedural line that bends progressively.
        let shapeWeight = 0;
        const direction = new T.Vector3(), curlAxis = this.right.clone();
        let curl = 0;
        if (grabbed && !tailGrip) {
            direction.copy(this.bodyUp).multiplyScalar(-.75).addScaledVector(this.forward, -.55);
            curl = -(1.8 + anticipation * 2);
            shapeWeight = .8;
        }
        else if (!grabbed && s.mode === 1) {
            const speed = v.length();
            // Streams behind the motion but keeps an elegant arc rather than a straight pole.
            direction.copy(speed > .5 ? v.clone().normalize().negate() : this.forward.clone().negate()).multiplyScalar(.6)
                .addScaledVector(this.forward, -.6).addScaledVector(this.bodyUp, -.2);
            curl = -(1.5 + .6 * flightStretch);
            shapeWeight = .55 + .35 * smooth(2, 8, speed);
            if (falling && target && target.distance < 3.2) {
                // Near miss: the tail reaches for the nearest span like a third arm.
                direction.lerp(target.point.clone().sub(pose[0]).normalize(), .8);
                curl = -.3;
                shapeWeight = .95;
            }
        }
        if (landing > .02) {
            direction.lerp(this.bodyUp.clone().multiplyScalar(.45).addScaledVector(this.forward, -.85), landing);
            curl = T.MathUtils.lerp(curl, -2.4, landing);
            shapeWeight = Math.max(shapeWeight, .6 * landing);
        }
        const desired = pose.map(q => q.clone());
        if (shapeWeight > 0 && direction.lengthSq() > 1e-6) {
            direction.normalize();
            const d = direction.clone();
            const point = pose[0].clone();
            for (let i = 1; i < n; i++) {
                const t = i / (n - 1);
                d.copy(direction).applyAxisAngle(curlAxis, curl * Math.pow(t, 1.4));
                point.addScaledVector(d, this.tailLength[i - 1]);
                desired[i].lerp(point, shapeWeight);
            }
        }
        // Verlet chain with a kinematic base, pose springs that weaken toward the tip, and gravity.
        const step = Math.min(dt, 1 / 30), g = new T.Vector3(0, -6 * step * step, 0);
        x[0].copy(pose[0]);
        for (let i = 1; i < n; i++) {
            const t = i / (n - 1), velocity = x[i].clone().sub(previous[i]).multiplyScalar(.86);
            previous[i].copy(x[i]);
            x[i].add(velocity).add(g);
            x[i].lerp(desired[i], clamp((.42 - .3 * t) * step * 60, 0, 1));
        }
        // Tail grip: the last segments coil around the bark; the rest of the chain is solved to reach them.
        let pinned = 0;
        if (tailGrip && contact) {
            const coil = this.coil(contact, pose[0], Math.min(6, n - 4));
            const blend = smooth(0, .12, this.catchAge);
            pinned = coil.length;
            for (let k = 0; k < pinned; k++)
                x[n - pinned + k].lerp(coil[k], blend);
            this.fabrik(x, n - pinned, pose[0]);
            // When the rope radius exceeds the tail, the body hangs a little closer to the bark.
            const gap = x[n - pinned].distanceTo(x[n - pinned - 1]) - this.tailLength[n - pinned - 1];
            if (gap > .02) {
                const pull = x[n - pinned].clone().sub(pose[0]).normalize().multiplyScalar(Math.min(.6, gap) * blend);
                this.root.position.add(pull);
                this.root.updateMatrixWorld(true);
                for (let i = 0; i < n - pinned; i++)
                    x[i].add(pull);
                this.fabrik(x, n - pinned, wp(bones[0], new T.Vector3()));
            }
        }
        else {
            // Length constraints, follow-the-leader from the base.
            for (let i = 1; i < n; i++) {
                const d = x[i].clone().sub(x[i - 1]), length = d.length() || 1;
                x[i].copy(x[i - 1]).addScaledVector(d, this.tailLength[i - 1] / length);
            }
        }
        // A chain that went non-finite or flew apart (e.g. a teleport between frames) re-seeds from the clip.
        if (x.some(q => !Number.isFinite(q.x + q.y + q.z) || q.distanceToSquared(pose[0]) > 9)) {
            this.tailReady = false;
            return;
        }
        // A light relaxation pass removes kinks the skinning would show as creases.
        if (!pinned)
            for (let i = 1; i < n - 1; i++)
                x[i].lerp(_a.copy(x[i - 1]).add(x[i + 1]).multiplyScalar(.5), .25);
        for (let i = 0; i < n - 1; i++)
            this.aim(bones[i], wp(bones[i + 1], new T.Vector3()), x[i + 1]);
    }
    /** Points of a tail coil wrapped around the contact, starting on the body's side and passing over the crown. */
    private coil(c: Contact, from: T.Vector3, count: number) {
        const up = this.across(c, from), f = new T.Vector3().crossVectors(c.axis, up);
        if (f.dot(this.forward) < 0)
            f.negate();
        const R = c.radius + .035, segment = this.tailLength[this.tailLength.length - 1] || .07;
        const points: T.Vector3[] = [];
        let angle = -.35;
        for (let k = 0; k < count; k++) {
            const radial = f.clone().multiplyScalar(-Math.cos(angle)).addScaledVector(up, Math.sin(angle));
            points.push(c.center.clone().addScaledVector(radial, R).addScaledVector(c.axis, .035 * angle));
            angle += segment / R;
        }
        return points;
    }
    /** FABRIK for the first `count` points between a fixed base and the fixed point at index `count`. */
    private fabrik(x: T.Vector3[], count: number, base: T.Vector3) {
        const L = this.tailLength;
        for (let iteration = 0; iteration < 6; iteration++) {
            for (let i = count - 1; i >= 1; i--) {
                const d = x[i].clone().sub(x[i + 1]), length = d.length() || 1;
                x[i].copy(x[i + 1]).addScaledVector(d, L[i] / length);
            }
            x[0].copy(base);
            for (let i = 1; i < count; i++) {
                const d = x[i].clone().sub(x[i - 1]), length = d.length() || 1;
                x[i].copy(x[i - 1]).addScaledVector(d, L[i - 1] / length);
            }
        }
    }
}
